/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/react'
import { Modal, message } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { font, weightSemiBold } from 'src/components/css'
import { primaryButtonStyles } from '../styles/shared'
import { useAiSuggest, type AiSuggestion } from '../hooks/useAiSuggest'
import { useAiSuggestActions } from '../hooks/useAiSuggestActions'
import AiSuggestKpiCard from './AiSuggestKpiCard'
import AiSuggestRow from './AiSuggestRow'

const modalContentStyles = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 70vh;
  overflow-y: auto;
  padding: 4px;
`

const headerStyles = css`
  ${font.body};
  font-weight: ${weightSemiBold};
  font-size: 16px;
  color: ${colors.WHITE};
  margin: 0;
`

const subHeaderStyles = css`
  ${font.body};
  font-size: 12px;
  color: ${colors.NEUTRAL_70};
  margin: 0 0 8px 0;
`

const emptyStateStyles = css`
  ${font.body};
  text-align: center;
  padding: 32px;
  color: ${colors.NEUTRAL_70};
`

const footerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid ${colors.SECONDARY_300};
`

const errorStyles = css`
  ${font.body};
  background-color: ${colors.ERROR_300}1A;
  border: 1px solid ${colors.ERROR_300};
  border-radius: 6px;
  color: ${colors.ERROR_300};
  padding: 12px 16px;
  font-size: 13px;
`

interface AiSuggestModalProps {
  open: boolean
  onClose: () => void
  flightId: string | null
  /** Called after any successful accept (single or bulk) so the constraints table can refresh. */
  onAccepted?: () => void | Promise<void>
}

/**
 * AI Suggest review modal. Phase 1 wires the contract end-to-end against
 * scheduler-python's canned suggestions. The user can Accept (unchanged
 * or edited), Reject, or Bulk-Accept. Each terminal action removes the
 * row locally; closing the modal abandons any pending rows (they expire
 * server-side at created_at + 24h).
 */
export const AiSuggestModal: React.FC<AiSuggestModalProps> = ({
  open,
  onClose,
  flightId,
  onAccepted,
}) => {
  'use memo'

  const {
    suggestions,
    isLoading,
    error,
    errorStatus,
    fetchSuggestions,
    removeLocally,
  } = useAiSuggest({ flightId })

  const { acceptMany, isMutating } = useAiSuggestActions({
    flightId,
    onAccepted,
  })

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  // Per-row edits collected from inline param editors. Keyed by suggestion id.
  // Submitted alongside the bulk-accept payload so admins can edit + accept
  // in one shot without per-row Accept buttons.
  const [editsById, setEditsById] = useState<
    Map<string, Record<string, unknown>>
  >(new Map())

  // Fetch on open. Reset selection when the modal closes.
  useEffect(() => {
    if (!open || !flightId) {
      return
    }
    setSelectedIds(new Set())
    setEditsById(new Map())
    fetchSuggestions().catch(() => {
      /* surfaced via `error` state */
    })
  }, [open, flightId, fetchSuggestions])

  const pendingSuggestions = useMemo(
    () => suggestions.filter((s) => s.status === 'pending'),
    [suggestions]
  )

  const handleBulkSelectChange = (id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (selected) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }

  const handleEditedParamsChange = (
    id: string,
    editedParams: Record<string, unknown> | null
  ) => {
    setEditsById((prev) => {
      const next = new Map(prev)
      if (editedParams === null) {
        next.delete(id)
      } else {
        next.set(id, editedParams)
      }
      return next
    })
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(pendingSuggestions.map((s) => s.id)))
    } else {
      setSelectedIds(new Set())
    }
  }

  const handleBulkAccept = async () => {
    if (selectedIds.size === 0) {
      return
    }
    try {
      const acceptances = Array.from(selectedIds).map((id) => {
        const item: {
          suggestion_uid: string
          edited_parameters?: Record<string, unknown>
        } = { suggestion_uid: id }
        const edits = editsById.get(id)
        if (edits) {
          item.edited_parameters = edits
        }
        return item
      })
      await acceptMany(acceptances)
      const acceptedSet = new Set(selectedIds)
      acceptedSet.forEach((id) => removeLocally(id))
      setSelectedIds(new Set())
      setEditsById(new Map())
      message.success(`Accepted ${acceptedSet.size} suggestion(s).`)
    } catch (err) {
      message.error('Accept failed. Try again.')
    }
  }

  const allSelected =
    pendingSuggestions.length > 0 &&
    pendingSuggestions.every((s) => selectedIds.has(s.id))

  const renderBody = () => {
    if (isLoading && suggestions.length === 0) {
      return <div css={emptyStateStyles}>Computing suggestions...</div>
    }

    if (error) {
      const friendlyMessage =
        errorStatus === 422
          ? 'AI Suggest is not yet available for this organization.'
          : errorStatus === 503
          ? 'AI Suggest service is temporarily unreachable. Try again shortly.'
          : 'Could not fetch suggestions. Try again.'
      return <div css={errorStyles}>{friendlyMessage}</div>
    }

    if (pendingSuggestions.length === 0) {
      return (
        <div css={emptyStateStyles}>
          No new suggestions for this flight right now.
        </div>
      )
    }

    return pendingSuggestions.map((s: AiSuggestion) => (
      <AiSuggestRow
        key={s.id}
        suggestion={s}
        bulkSelected={selectedIds.has(s.id)}
        onBulkSelectedChange={handleBulkSelectChange}
        onEditedParamsChange={handleEditedParamsChange}
        disabled={isMutating}
      />
    ))
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
      centered
      title={null}
      maskClosable={!isMutating}
    >
      <h2 css={headerStyles}>AI Suggest Constraints</h2>
      <p css={subHeaderStyles}>
        Review the proposed constraints. Tick the ones you want and click
        &ldquo;Accept selected&rdquo; — anything left unselected stays out of
        your flight.
      </p>

      <div
        css={css`
          margin-bottom: 12px;
        `}
      >
        <AiSuggestKpiCard hideWhenEmpty />
      </div>

      <div css={modalContentStyles}>{renderBody()}</div>

      {pendingSuggestions.length > 0 && !error && (
        <div css={footerStyles}>
          <label
            css={css`
              ${font.body};
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 12px;
              color: ${colors.NEUTRAL_70};
              cursor: pointer;
            `}
          >
            <input
              type='checkbox'
              checked={allSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
              disabled={isMutating}
              css={css`
                width: 16px;
                height: 16px;
                cursor: pointer;
                accent-color: ${colors.PRIMARY};
              `}
            />
            Select all ({pendingSuggestions.length})
          </label>
          <button
            type='button'
            css={primaryButtonStyles}
            onClick={handleBulkAccept}
            disabled={selectedIds.size === 0 || isMutating}
          >
            Accept selected ({selectedIds.size})
          </button>
        </div>
      )}
    </Modal>
  )
}

export default AiSuggestModal
